import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { Flex, FormInstance, Spin, Tooltip } from "antd";
import {
  QuestionBeProps,
  QuestionProps,
  QuizProps,
} from "../../../../../../../types/course";
import EmptyComponent from "../../../../../../../components/empty";
import { convertNumberingTypeToText } from "../../../../../../../utils/format";
import {
  JSON_SUB_TYPE,
  QuestionTypeEnum,
} from "../../../../../../../constants/course";
import { isEmpty, toNumber } from "lodash";
import {
  HoursGlassIcon,
  QuestionMarkIcon,
} from "../../../../../../../components/icons";
import Countdown from "../../../../../../../components/countdown/CountDown";
import useKeyword from "../../../../../../../hooks/useKeyword";
import { startSingleQuiz } from "../../../../../../../service/learning-course";
import { Link } from "react-router-dom";
import FormulaRenderer from "../../../../../../../components/math-formula";
import { SortableList } from "../../../../../../SortableList";
import QuestionContentTitle from "../../components/QuestionContentTitle";
import { sanitizeLatexInput } from "../../../../../../../utils/app";

interface ConnectProps extends QuestionProps {
  data?: QuizProps;
  form?: FormInstance<any>;
  onChange?: (value: any) => void;
}
function Connect({
  data,
  pause,
  configs,
  disabled,
  hiddenIndex,
  initialData,
  onFinish = () => {},
  onChange = () => {},
}: ConnectProps) {
  const { t } = useTranslation();
  const initialTime =
    data?.timeToCompleted || configs?.actionConfig?.timeForQuiz || 0;
  const [answers, setAnswers] = useState<any>([]);

  const connectRef = useRef<HTMLDivElement | null>(null);
  const [itemHeight, setItemHeight] = useState<number | undefined>(0);
  const [itemSwapped, setItemSwapped] = useState<number[]>([]);

  const [constAnswer, setConstAnswer] = useState<any[]>([]);
  const [dirtyAns, setDirtyAns] = useState<string[]>([]);
  const blockId = toNumber(useKeyword("blockId"));
  const currentScreen = useKeyword("screen");
  const isSequentiallyQuiz = configs?.generalConfig?.sequentiallyQuiz;
  const [loading, setLoading] = useState<boolean>(false);
  const [canStart, setCanStart] = useState<boolean>(false);

  useEffect(() => {
    // Xử lý câu hỏi có đếm thời gian riêng
    if (currentScreen === "train") {
      if (!isEmpty(data) && isSequentiallyQuiz && initialTime) {
        const startSingle = async () => {
          try {
            setLoading(true);
            await startSingleQuiz({
              blockId: blockId,
              quizId: data?.id,
            });
            setCanStart(true);
          } catch (err: any) {
          } finally {
            setLoading(false);
          }
        };
        startSingle();
      }
    }
  }, [isSequentiallyQuiz]);

  useEffect(() => {
    setItemHeight(connectRef.current?.clientHeight);
  }, [connectRef.current, window.innerWidth]);

  useEffect(() => {
    if (data?.questions && data?.questions?.length > 0) {
      setAnswers(
        data?.questions?.map((ans: QuestionBeProps) => ({
          id: ans?.content?.right.key,
          value: ans?.content?.right.content,
        }))
      );
      setConstAnswer(
        data?.questions?.map((ans: QuestionBeProps) => ({
          id: ans?.content?.right.key,
          value: ans?.content?.right.content,
        }))
      );
    }
  }, [data?.id]);

  useEffect(() => {
    if (answers?.length) {
      const dirtyArr: string[] = [];
      for (let i = 0; i < answers.length; i++) {
        if (JSON.stringify(constAnswer[i]) !== JSON.stringify(answers[i])) {
          dirtyArr.push(constAnswer[i]?.id, answers[i]?.id);
        }
      }
      setDirtyAns((prevState: string[]) => [...prevState, ...dirtyArr]);
    }
  }, [answers]);

  useEffect(() => {
    // Trường hợp chuyển sang câu hỏi khác xong quay lại
    if (!isEmpty(initialData)) {
      const initData = initialData?.answer?.map((item: any) => ({
        id: item?.right?.key,
        value: item?.right?.content,
      }));
      setAnswers(initData);

      // các item đã swap
      if (initialData?.answer[0]?.swapped)
        setItemSwapped((prevState) => [
          ...prevState,
          ...initialData?.answer[0]?.swapped,
        ]);
    }
  }, [initialData]);

  const handleDragChange = (fieldValues: any) => {
    setAnswers(fieldValues);

    // các item đã swap
    let newItemsSwapped: any = [];
    for (let i = 0; i < fieldValues?.length; i++) {
      if (answers[i]?.id !== fieldValues[i].id) {
        newItemsSwapped.push(fieldValues[i].id);
      }
    }
    setItemSwapped((prevState) => [...prevState, ...newItemsSwapped]);

    // item changed
    if (data) {
      const answer = data?.questions?.map(
        (item: QuestionBeProps, index: number) => ({
          left: { ...item.content?.left },
          right: {
            key: fieldValues[index]?.id,
            content: fieldValues[index]?.value,
          },
          swapped: [...itemSwapped, ...newItemsSwapped],
        })
      );
      onChange({
        [`connect-${data?.id}-${QuestionTypeEnum.CONNECT}-${JSON_SUB_TYPE.ConnectingRequest}`]:
          answer?.map((ans: any) => ({
            left: ans?.left,
            right: ans?.right,
          })),
      });
    }
  };

  return (
    <div>
      <Spin spinning={loading}>
        <div
          className="question-header mb-3"
          style={{
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            padding: "15px",
          }}
        >
          {configs?.actionConfig?.timeForQuiz &&
          configs?.generalConfig?.sequentiallyQuiz ? (
            <div className="question-time flex align-center gap-8px">
              <HoursGlassIcon />
              <div className="text-primary font-size-14 line-height-20 font-weight-6 flex align-center gap-8px">
                {t("Trả lời câu hỏi trong:", "Trả lời câu hỏi trong:")}
                <Countdown
                  pause={pause}
                  initialTime={canStart ? toNumber(initialTime) : 0}
                  onFinish={onFinish}
                />
              </div>
            </div>
          ) : (
            ""
          )}
          <div className="question-title" style={{ marginBottom: 0 }}>
            {hiddenIndex ? null : (
              <Flex
                justify="space-between"
                align="center"
                className="font-size-16 line-height-24 font-weight-6"
              >
                {data?.settingGeneral?.requiredAnswer ? (
                  <span className="text-danger">*</span>
                ) : (
                  ""
                )}
                <div className="flex-1 ml-1">
                  {t("Câu hỏi", "Câu hỏi")} {toNumber(data?.index) + 1}:{" "}
                </div>
                {configs?.generalConfig?.showQuizScore && (
                  <span className="total-point font-size-14">
                    {`(${data?.totalPoints || 0} điểm)`}
                  </span>
                )}
              </Flex>
            )}
            <Flex align="flex-start" gap={8}>
              <QuestionContentTitle content={data?.question as string} />
              {data?.settingHint?.isDisplayInstruction && data?.isShowHint ? (
                <Tooltip
                  placement="bottom"
                  title={
                    <div>
                      {data?.settingHint?.content ? (
                        <p className="text-14 font-weight-5">
                          {t("Hướng dẫn:", "Hướng dẫn:")} {data?.settingHint?.content}
                        </p>
                      ) : (
                        "Không có hướng dẫn"
                      )}

                      {data?.settingHint?.schema ? (
                        <p className="font-size-14 mt-2">
                          {t("Đường dẫn tham khảo:", "Đường dẫn tham khảo:")}{" "}
                          <Link
                            target="_blank"
                            to={`${data?.settingHint?.uri}${data?.settingHint?.schema}`}
                          >
                            {`${data?.settingHint?.uri}${data?.settingHint?.schema}`}
                          </Link>
                        </p>
                      ) : (
                        ""
                      )}
                    </div>
                  }
                >
                  <span className="quest-icon text-secondary w-max-content">
                    <QuestionMarkIcon />
                  </span>
                </Tooltip>
              ) : (
                ""
              )}
            </Flex>
          </div>
        </div>
        <div className="flex">
          <div className="connect-quest justify-space-between w-50 mr-3">
            {data?.questions && data?.questions?.length > 0 ? (
              data?.questions?.map((quiz: QuestionBeProps, index) => (
                <div
                  className={`connect-quest-item ${
                    dirtyAns?.includes(quiz?.content?.right?.key) &&
                    "connect-quest-item-connecting"
                  }`}
                  key={`${quiz.uuid}-${index}-left`}
                  ref={connectRef}
                >
                  <Flex className="font-size-14 line-height-24">
                    {convertNumberingTypeToText(
                      index + 1,
                      data?.settingGeneral?.numberingType
                    )}
                    .{" "}
                    <FormulaRenderer
                      className="ml-1 quest-content-display"
                      htmlString={`${sanitizeLatexInput(quiz?.content?.left?.content)}`}
                    />
                  </Flex>
                </div>
              ))
            ) : (
              <div className="center">
                <EmptyComponent description={t("Không có nội dung", "Không có nội dung")} />
              </div>
            )}
          </div>
          <div className="w-50 ml-3">
            <SortableList
              items={answers}
              className="gap-16 justify-space-between"
              disabled={disabled}
              onChange={handleDragChange}
              renderItem={(record: any) => (
                <SortableList.Item
                  className={`connect-item ${
                    itemSwapped?.includes(record?.id) && `connect-item-active`
                  }`}
                  id={record.id}
                >
                  <SortableList.DragHandle />
                  <div className="font-size-14">
                    <FormulaRenderer
                      className="ml-1 quest-content-display"
                      htmlString={sanitizeLatexInput(record.value)}
                    />
                  </div>
                </SortableList.Item>
              )}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
}

export default Connect;
